import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { DotLoading, Toast } from 'antd-mobile';
import type {
  DrawTimeConfig,
  LatestDrawResponse,
  LotterySiteSettings,
  PlayType,
  ZiliaoGroup,
} from '../types/lottery';
import { GAMES } from '../types/lottery';
import {
  getDrawTimeConfig,
  finalizePendingDraws,
  getLatestDraw,
  getLotterySiteSettings,
  getPlayTypes,
  getZiliaoAll,
} from '../api/lottery';
import { formatPeriod, shortPeriod, splitDrawBalls } from '../utils/ball';
import { applySiteMeta } from '../utils/siteMeta';
import {
  readCachedSiteSettings,
  resolveSiteName,
  resolveSiteShortName,
  writeCachedSiteSettings,
} from '../utils/siteSettingsCache';
import { resolveUploadUrl } from '../utils/uploadUrl';
import LotteryBall from '../components/LotteryBall';
import AdCarousel from '../components/AdCarousel';
import ImageBindingSceneCard from '../components/ImageBindingSceneCard';
import PlayRuleLegend from '../components/PlayRuleLegend';
import {
  buildTimezoneAwareTime,
  compactPlayTitle,
  hasVisualItems,
  normalizePlayTag,
  renderJudge,
  renderPredictionTitle,
  resolveGameParam,
  resolveLotteryTimezone,
} from './Home';
import './Home.css';
import './AllPlaysHome.css';

const AllPlaysHome: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const game = resolveGameParam(searchParams.get('game'));
  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState<LatestDrawResponse | null>(null);
  const [drawTimeConfig, setDrawTimeConfig] = useState<DrawTimeConfig | null>(null);
  const [playTypes, setPlayTypes] = useState<PlayType[]>([]);
  const [groups, setGroups] = useState<ZiliaoGroup[]>([]);
  const [siteSettings, setSiteSettings] = useState<LotterySiteSettings | null>(() => readCachedSiteSettings());
  const [selectedTag, setSelectedTag] = useState('');
  const [countdown, setCountdown] = useState('');
  const [showBackTop, setShowBackTop] = useState(false);
  const requestRef = useRef(0);
  const latestRequestRef = useRef(0);
  const currentGameRef = useRef(game);
  const siteSettingsRequestRef = useRef(0);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  currentGameRef.current = game;

  const fetchData = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const [latestData, drawTimeData, playTypeData, groupData] = await Promise.all([
        getLatestDraw(game),
        getDrawTimeConfig(game),
        getPlayTypes(game),
        getZiliaoAll(game, 12),
      ]);
      if (requestId !== requestRef.current) {
        return;
      }
      setDrawTimeConfig(drawTimeData);
      setPlayTypes(playTypeData);
      setGroups(groupData);
      // 首次加载直接显示当前最新一期，不播动画
      setLatest(latestData);
    } catch {
      if (requestId !== requestRef.current) {
        return;
      }
      Toast.show({ content: '加载失败', icon: 'fail' });
      setLatest(null);
      setDrawTimeConfig(null);
      setPlayTypes([]);
      setGroups([]);
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  }, [game]);

  const refreshLatestDraw = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      const latestData = await getLatestDraw(game);
      if (requestId !== latestRequestRef.current || currentGameRef.current !== game) {
        return;
      }
      const phase = drawAnimationPhaseRef.current;

      // revealing 阶段：揭示中，丢弃所有 polling 数据
      if (phase === 'revealing') {
        return;
      }

      // syncing 阶段：持续刷新暂存，看号码是否到位、是否可推进 revealing
      if (phase === 'syncing') {
        pendingLatestRef.current = latestData;
        tryEnterRevealingRef.current();
        return;
      }

      // idle 阶段
      // awaiting_result：动画播完了，等后端 is_drawing=false 才刷新一次
      if (drawCycleRef.current === 'awaiting_result') {
        if (!latestData.is_drawing) {
          drawCycleRef.current = 'normal';
          setLatest(latestData);
        }
        return;
      }

      // normal：检测 is_drawing 翻转 true → 进入 syncing
      if (latestData.is_drawing) {
        const baselinePeriod = String(latest?.prev?.period || '');
        pendingLatestRef.current = latestData;
        enterSyncingRef.current(baselinePeriod);
        tryEnterRevealingRef.current();
        return;
      }

      setLatest(latestData);
    } catch {
      // ignore latest draw refresh failures
    }
  }, [game, latest?.prev?.period]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // 切彩种时重置开奖周期状态
  useEffect(() => {
    drawCycleRef.current = 'normal';
    animateBaselinePeriodRef.current = '';
    syncingStartedAtRef.current = 0;
    pendingLatestRef.current = null;
  }, [game]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void refreshLatestDraw();
    }, 5000);
    return () => {
      window.clearInterval(timer);
    };
  }, [refreshLatestDraw]);

  useEffect(() => {
    const rawGame = searchParams.get('game');
    if (rawGame !== game) {
      setSearchParams({ game }, { replace: true });
    }
  }, [game, searchParams, setSearchParams]);

  useEffect(() => {
    const requestId = ++siteSettingsRequestRef.current;
    getLotterySiteSettings()
      .then((res) => {
        if (requestId !== siteSettingsRequestRef.current) {
          return;
        }
        setSiteSettings(writeCachedSiteSettings(res));
      })
      .catch(() => {
        if (requestId !== siteSettingsRequestRef.current) {
          return;
        }
      });
  }, []);

  const orderedGroups = useMemo(() => {
    const groupMap = new Map(groups.map((group) => [group.tag, group]));
    const ordered = playTypes
      .map((item) => groupMap.get(item.tag))
      .filter((item): item is ZiliaoGroup => Boolean(item));

    if (ordered.length > 0) {
      return ordered;
    }
    return groups;
  }, [groups, playTypes]);

  const playHitEffectModeByTag = useMemo(
    () =>
      playTypes.reduce<Record<string, PlayType['hit_effect_mode']>>((acc, item) => {
        acc[item.tag] = item.hit_effect_mode;
        const normalizedTag = normalizePlayTag(item.tag);
        if (!acc[normalizedTag]) {
          acc[normalizedTag] = item.hit_effect_mode;
        }
        return acc;
      }, {}),
    [playTypes],
  );

  useEffect(() => {
    if (orderedGroups.length === 0) {
      setSelectedTag('');
      return;
    }
    if (!selectedTag || !orderedGroups.some((group) => group.tag === selectedTag)) {
      setSelectedTag(orderedGroups[0].tag);
    }
  }, [orderedGroups, selectedTag]);

  const drawTimezone = resolveLotteryTimezone(drawTimeConfig);
  const currentOpenDisplayTime = buildTimezoneAwareTime(latest?.prev?.opentime, drawTimezone)?.format('YYYY-MM-DD HH:mm') || '--';
  const nextOpenDisplayTime = buildTimezoneAwareTime(latest?.next?.opentime, drawTimezone)?.format('YYYY-MM-DD HH:mm') || '--';
  const currentGame = GAMES.find((item) => item.code === game) || GAMES[0];
  const siteName = resolveSiteName(siteSettings) || '彩票网站';
  const siteShortName = resolveSiteShortName(siteSettings) || siteName;
  const siteDescription = siteSettings?.site_description?.trim() || '彩势情报';
  const siteLogo = resolveUploadUrl(siteSettings?.site_logo);
  const issueLabel = latest?.prev?.period ? shortPeriod(latest.prev.period) : '--';
  const showInlineMiddleImageAd = game === 'xglhc' || game === 'amlhc';
  const showBottomMiddleImageAd = game === 'amssc' || game === 'xamlhc';
  const [revealedDrawBallCount, setRevealedDrawBallCount] = useState(0);
  const lastAnimatedPeriodRef = useRef<string>('');
  const [drawAnimationPhase, setDrawAnimationPhaseRaw] = useState<'idle' | 'syncing' | 'revealing'>('idle');
  const drawAnimationPhaseRef = useRef<'idle' | 'syncing' | 'revealing'>('idle');
  const setDrawAnimationPhase = useCallback((phase: 'idle' | 'syncing' | 'revealing') => {
    drawAnimationPhaseRef.current = phase;
    setDrawAnimationPhaseRaw(phase);
  }, []);
  const pendingLatestRef = useRef<LatestDrawResponse | null>(null);
  const revealIntervalRef = useRef<number | null>(null);
  // 开奖周期状态：normal=可触发动画；awaiting_result=动画播完，等后端 is_drawing=false 再刷新
  const drawCycleRef = useRef<'normal' | 'awaiting_result'>('normal');
  // syncing 阶段开始时间戳，用于"至少同步 10 秒"的视觉节奏
  const syncingStartedAtRef = useRef<number>(0);
  // 触发动画时的 prev.period（基线），用于判断"号码是否已采集到新一期"
  const animateBaselinePeriodRef = useRef<string>('');
  const drawSyncChars = useMemo(() => Array.from('正在同步开奖中'), []);
  const { normalBalls, specialBalls } = splitDrawBalls(
    latest?.prev?.format || [],
    latest?.prev?.regular_count,
    latest?.prev?.special_count,
  );
  const drawBallItems = useMemo(
    () => [
      ...normalBalls.map((ball) => ({ ball, isSpecial: false })),
      ...specialBalls.map((ball) => ({ ball, isSpecial: true })),
    ],
    [normalBalls, specialBalls],
  );

  const clearRevealTimers = useCallback(() => {
    if (revealIntervalRef.current !== null) {
      window.clearInterval(revealIntervalRef.current);
      revealIntervalRef.current = null;
    }
  }, []);

  // 进入 syncing：显示"正在同步开奖中"，等号码到位
  const enterSyncing = useCallback((baselinePeriod: string) => {
    clearRevealTimers();
    syncingStartedAtRef.current = Date.now();
    animateBaselinePeriodRef.current = baselinePeriod;
    setRevealedDrawBallCount(0);
    setDrawAnimationPhase('syncing');
  }, [clearRevealTimers, setDrawAnimationPhase]);

  // 检查是否可从 syncing 推进到 revealing：满 10s + 拿到了新号码
  const tryEnterRevealing = useCallback(() => {
    if (drawAnimationPhaseRef.current !== 'syncing') {
      return;
    }
    const pending = pendingLatestRef.current;
    if (!pending) {
      return;
    }
    const newPeriod = String(pending.prev?.period || '');
    if (newPeriod === '' || newPeriod === animateBaselinePeriodRef.current) {
      return; // 号码还没到位
    }
    if ((pending.prev?.format?.length || 0) === 0) {
      return;
    }
    const elapsed = Date.now() - syncingStartedAtRef.current;
    if (elapsed < 10000) {
      return; // 同步动画至少播 10s
    }
    pendingLatestRef.current = null;
    lastAnimatedPeriodRef.current = newPeriod;
    setLatest(pending);
    setDrawAnimationPhase('revealing');
    setRevealedDrawBallCount(1);
    const total = pending.prev?.format?.length || 7;
    revealIntervalRef.current = window.setInterval(() => {
      setRevealedDrawBallCount((current) => {
        const nextCount = current + 1;
        if (nextCount >= total) {
          if (revealIntervalRef.current !== null) {
            window.clearInterval(revealIntervalRef.current);
            revealIntervalRef.current = null;
          }
          setDrawAnimationPhase('idle');
          drawCycleRef.current = 'awaiting_result';
          void finalizePendingDraws(game);
        }
        return nextCount;
      });
    }, 7000);
  }, [setDrawAnimationPhase, game]);

  const tryEnterRevealingRef = useRef(tryEnterRevealing);
  tryEnterRevealingRef.current = tryEnterRevealing;
  const enterSyncingRef = useRef(enterSyncing);
  enterSyncingRef.current = enterSyncing;

  useEffect(() => {
    if (!latest?.next?.opentime) {
      setCountdown(drawAnimationPhase !== 'idle' ? '开奖中' : '等待开奖');
      return;
    }

    let timer = 0;
    const tick = () => {
      const target = buildTimezoneAwareTime(latest.next.opentime, drawTimezone);
      if (!target) {
        setCountdown('');
        return;
      }
      if (drawAnimationPhase !== 'idle') {
        setCountdown('开奖中');
        return;
      }
      const diff = target.diff(dayjs().tz(drawTimezone), 'second');
      if (diff <= 0) {
        // 倒计时归零本身不代表"正在开奖"——只有后端 is_drawing=true 才是真的在开奖窗口里
        setCountdown(latest.is_drawing ? '开奖中' : '等待开奖');
        return;
      }
      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      const pad = (value: number) => String(value).padStart(2, '0');
      setCountdown(d > 0 ? `${d}天 ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`);
    };

    tick();
    timer = window.setInterval(tick, 1000);
    return () => {
      if (timer) {
        window.clearInterval(timer);
      }
    };
  }, [drawAnimationPhase, drawTimezone, latest?.next?.opentime, latest?.is_drawing]);

  useEffect(() => {
    if (drawAnimationPhase !== 'idle') {
      return undefined;
    }
    const currentPeriod = String(latest?.prev?.period || '').trim();
    if (!currentPeriod || drawBallItems.length === 0) {
      setRevealedDrawBallCount(drawBallItems.length);
      return undefined;
    }
    // 由开奖动画接管的期号不再走快揭示
    if (lastAnimatedPeriodRef.current === currentPeriod) {
      setRevealedDrawBallCount(drawBallItems.length);
      return undefined;
    }
    lastAnimatedPeriodRef.current = currentPeriod;
    setRevealedDrawBallCount(drawBallItems.length);
    return undefined;
  }, [drawAnimationPhase, drawBallItems.length, latest?.prev?.period]);

  useEffect(() => clearRevealTimers, [clearRevealTimers]);

  useEffect(() => {
    applySiteMeta(siteSettings, {
      title: `${siteName} - ${currentGame.name} 全部玩法`,
      appName: siteName,
      appShortName: siteShortName,
      fallbackDescription: `${siteName} ${currentGame.name} 全部玩法资料页，提供实时开奖、分类跳转和历史走势。`,
      fallbackKeywords: `${siteName},${currentGame.name},${siteShortName},玩法,开奖,走势,历史记录`,
    });
  }, [currentGame.name, siteName, siteSettings, siteShortName]);

  useEffect(() => {
    if (orderedGroups.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const activeEntry = visibleEntries[0];
        const activeTag = activeEntry?.target.getAttribute('data-tag');
        if (activeTag) {
          setSelectedTag((prev) => (prev === activeTag ? prev : activeTag));
        }
      },
      {
        rootMargin: '-108px 0px -55% 0px',
        threshold: [0, 0.15, 0.35],
      },
    );

    orderedGroups.forEach((group) => {
      const node = sectionRefs.current[group.tag];
      if (node) {
        observer.observe(node);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [orderedGroups]);

  useEffect(() => {
    let raf = 0;
    const syncScrollState = () => {
      if (raf) {
        return;
      }
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        setShowBackTop(window.scrollY > window.innerHeight / 2);
      });
    };

    syncScrollState();
    window.addEventListener('scroll', syncScrollState, { passive: true });
    window.addEventListener('resize', syncScrollState);
    return () => {
      if (raf) {
        window.cancelAnimationFrame(raf);
      }
      window.removeEventListener('scroll', syncScrollState);
      window.removeEventListener('resize', syncScrollState);
    };
  }, []);

  const scrollToGroup = useCallback((tag: string) => {
    const node = sectionRefs.current[tag];
    if (!node) {
      return;
    }
    setSelectedTag(tag);
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleBackTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="home2-page allplay-page">
      <div className="home2-topbar home2-topbar--stacked">
        <div className="brand-pill">
          {siteLogo ? <img className="brand-pill__logo" src={siteLogo} alt={siteName} /> : null}
          <div className="brand-pill__text">
            <div className="brand-pill__title">{siteName}</div>
            <div className="brand-pill__subtitle">{siteDescription}</div>
          </div>
        </div>
      </div>

      <div className="game-strip">
        {GAMES.map((item) => (
          <button
            key={item.code}
            type="button"
            className={`game-strip-item ${item.code === game ? 'is-active' : ''}`}
            onClick={() => setSearchParams({ game: item.code }, { replace: true })}
          >
            {item.shortName}
          </button>
        ))}
      </div>

      <AdCarousel position="home_top" />

      <section className="issue-board">
        <div className="issue-board-head">
          <div>
            <div className="issue-board-kicker">{currentGame.name}</div>
            <div className="issue-board-period">{latest?.prev?.period ? formatPeriod(latest.prev.period) : '等待开奖'}</div>
          </div>
          <div className="issue-board-meta">
            <span className="meta-chip">{countdown || '同步中'}</span>
            <span className="meta-text">下期 {latest?.next?.period || '--'}</span>
          </div>
        </div>
        <div className="issue-board-balls">
          {drawAnimationPhase !== 'idle' ? (() => {
            const slotCount = Math.max(drawBallItems.length, drawSyncChars.length);
            const visibleBalls = drawAnimationPhase === 'syncing' ? 0 : Math.min(revealedDrawBallCount, drawBallItems.length);
            return Array.from({ length: slotCount }, (_, index) => {
              if (index < visibleBalls && drawBallItems[index]) {
                const { ball, isSpecial } = drawBallItems[index];
                return (
                  <LotteryBall
                    key={`anim-ball-${index}`}
                    ball={ball}
                    size="sm"
                    showSx
                    isSpecial={isSpecial}
                    reveal
                    revealDelayMs={0}
                  />
                );
              }
              return (
                <div
                  key={`anim-sync-${index}`}
                  className={`draw-sync-slot ${drawBallItems[index]?.isSpecial ? 'is-special' : ''}`}
                  aria-hidden="true"
                >
                  <div className="draw-sync-char">{drawSyncChars[index] || ''}</div>
                  <div className="draw-sync-subline" />
                </div>
              );
            });
          })() : drawBallItems.length === 0 ? (
            <div className="ball-placeholder">当前暂无可展示开奖号</div>
          ) : (
            drawBallItems.slice(0, revealedDrawBallCount).map(({ ball, isSpecial }, index) => (
              <LotteryBall
                key={`${isSpecial ? 'special' : 'normal'}-${ball.title}-${index}`}
                ball={ball}
                size="sm"
                showSx
                isSpecial={isSpecial}
                reveal
                revealDelayMs={0}
              />
            ))
          )}
        </div>
        <div className="issue-board-foot">
          <span>当前期号 {issueLabel}</span>
          <span>本期开奖 {currentOpenDisplayTime}</span>
          <span>下期开奖 {nextOpenDisplayTime}</span>
        </div>
      </section>

      <div className="home2-actions allplay-page-actions">
        <button type="button" className="action-button ghost" onClick={() => void fetchData()}>
          {loading ? '刷新中...' : '刷新数据'}
        </button>
        <button type="button" className="action-button" onClick={() => navigate(`/history?game=${game}`)}>
          查看历史开奖
        </button>
      </div>

      {game !== 'xglhc' && game !== 'amlhc' && (
      <section className="menu-board allplay-menu-board">
        <div className="allplay-menu-head">
          <div className="section-title">网站导航</div>
          <div className="allplay-menu-tip">点击可跳到对应玩法数据块</div>
        </div>
        <div className="allplay-menu-grid">
          {orderedGroups.map((group) => {
            const isActive = group.tag === selectedTag;
            return (
              <button
                key={group.tag}
                type="button"
                className={`menu-chip ${isActive ? 'is-active' : ''}`}
                onClick={() => scrollToGroup(group.tag)}
              >
                {compactPlayTitle(group.title, game)}
              </button>
            );
          })}
        </div>
      </section>
      )}

      <AdCarousel position="home_middle" />

      {/* 中部图片广告位 - 港六合和澳六合显示在中部 */}
      {showInlineMiddleImageAd && (
        <AdCarousel position="home_middle_image" displayMode="stack" />
      )}

      {game !== 'xglhc' && game !== 'amlhc' && (
      <div className={`allplay-stack${showBottomMiddleImageAd ? ' allplay-stack--with-bottom-ad' : ''}`}>
        {loading && orderedGroups.length === 0 ? (
          <div className="feature-board allplay-empty-state">
            <div className="records-loading">
              <DotLoading />
            </div>
          </div>
        ) : null}

        {!loading && orderedGroups.length === 0 ? (
          <div className="feature-board allplay-empty-state">
            <div className="records-empty">当前暂无玩法数据</div>
          </div>
        ) : null}

        {orderedGroups.map((group, index) => {
          const groupHitEffectMode = playHitEffectModeByTag[group.tag] || playHitEffectModeByTag[normalizePlayTag(group.tag)];
          const groupTitle = compactPlayTitle(group.title, game);
          return (
            <section
              key={group.tag}
              ref={(node) => {
                sectionRefs.current[group.tag] = node;
              }}
              data-tag={group.tag}
              className="feature-board allplay-board"
            >
              <div className="allplay-board-head">
                <div className="allplay-board-copy">
                  <div className="allplay-board-kicker">
                    {siteShortName} · {currentGame.shortName} · {String(index + 1).padStart(2, '0')}
                  </div>
                  <h2 className="allplay-board-title">{groupTitle}</h2>
                </div>
                <div className="allplay-board-side">
                  {group.hit_rate_text ? <span className="allplay-badge">{group.hit_rate_text}</span> : null}
                  <span className="allplay-badge allplay-badge--soft">
                    {group.hit_rate_percent != null ? `${group.hit_rate_percent}%` : `${group.records?.length || 0}期`}
                  </span>
                </div>
              </div>
              <PlayRuleLegend groups={group.rule_details} title="玩法规则对照" />

              <div className="record-list allplay-record-list">
                {loading ? (
                  <div className="records-loading">
                    <DotLoading />
                  </div>
                ) : group.records?.length ? (
                  group.records.map((record) => {
                    const visualMode = record.visual_payload?.hit_effect_mode || groupHitEffectMode;
                    const visualRecord = hasVisualItems(record);
                    return (
                      <div key={record.id} className={`record-row${visualRecord ? ' has-visual' : ''}`}>
                        <div className="record-period">{shortPeriod(record.period)}</div>
                        <div className="record-body">
                          <div className={`record-prediction${visualRecord ? ' is-visual' : ''}`}>
                            {visualRecord ? (
                              <ImageBindingSceneCard
                                items={record.visual_payload!.items}
                                hitEffectMode={visualMode}
                                backgroundImage={record.visual_payload?.background_image}
                                sceneWidth={record.visual_payload?.scene_width}
                                sceneHeight={record.visual_payload?.scene_height}
                                period={shortPeriod(record.period)}
                                suc={record.suc}
                                openTitle={record.open_title}
                              />
                            ) : (
                              renderPredictionTitle(
                                record.title,
                                group.tag,
                                record.open_title,
                                record.effect_values,
                                groupHitEffectMode,
                              )
                            )}
                          </div>
                          <div className="record-result">
                            <span className="record-result-label">开:</span>
                            <span className="record-result-value">{record.open_title || '未开奖'}</span>
                            {record.open_scope ? <span className="record-result-scope">{record.open_scope}</span> : null}
                          </div>
                        </div>
                        <div className="record-side">{renderJudge(record)}</div>
                      </div>
                    );
                  })
                ) : (
                  <div className="records-empty">当前玩法暂无数据</div>
                )}
              </div>
            </section>
          );
        })}
      </div>
      )}
      {showBottomMiddleImageAd && (
        <AdCarousel position="home_middle_image" displayMode="stack" />
      )}
      <button
        type="button"
        className={`home2-backtop${showBackTop ? ' is-visible' : ''}`}
        onClick={handleBackTop}
        aria-label="返回顶部"
      >
        回到顶部
      </button>
    </div>
  );
};

export default AllPlaysHome;
